import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { useProfileStore } from '@/stores/profileStore';
import {
 User,
 Briefcase,
 GraduationCap,
 Code,
 ExternalLink,
 ChevronDown,
 ChevronUp,
 Target,
 Settings,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TerminalLoader } from '@/components/ui/TerminalLoader';

export function ProfilePage() {
 const { profile, isLoading, fetchProfile } = useProfileStore();
 const [isExperienceExpanded, setIsExperienceExpanded] = useState(false);
 const navigate = useNavigate();

 useEffect(() => {
  fetchProfile();
 }, [fetchProfile]);

 if (isLoading) {
  return (
   <AppLayout>
    <TerminalLoader text='LOADING_PERSONNEL_FILE...' />
   </AppLayout>
  );
 }

 if (!profile) {
  return (
   <AppLayout>
    <div className='flex flex-col items-center justify-center h-[50vh] space-y-4'>
     <div className='text-muted-foreground font-mono'>
      USER_PROFILE_NOT_FOUND
     </div>
     <Button
      onClick={() => fetchProfile()}
      variant='outline'
      className='font-mono rounded-none'
     >
      RETRY_CONNECTION
     </Button>
    </div>
   </AppLayout>
  );
 }

 return (
  <AppLayout>
   {/* Header */}
   <div className='mb-12 border-b border-border pb-8'>
    <div className='flex items-center gap-2 mb-2'>
     <User className='w-4 h-4 text-primary' />
     <span className='font-mono text-xs text-primary'>PERSONNEL_FILE</span>
    </div>
    <div className='flex justify-between items-end'>
     <div>
      <h1 className='text-3xl font-bold font-mono tracking-tight mb-2 uppercase'>
       {profile.personalInfo.name || 'UNKNOWN_USER'}
      </h1>
      <p className='text-muted-foreground font-mono text-sm'>
       {profile.currentTitle} @ {profile.currentCompany}
      </p>
     </div>
     <div className='flex gap-2'>
      <Button
       variant='outline'
       className='font-mono border-primary/50 hover:bg-primary/10 hover:text-primary gap-2'
       onClick={() => navigate('/onboarding')}
      >
       UPDATE_PROFILE
      </Button>
      {profile.personalInfo.linkedinUrl && (
       <a
        href={profile.personalInfo.linkedinUrl}
        target='_blank'
        rel='noopener noreferrer'
       >
        <Button variant='outline' size='icon' className='rounded-none'>
         <ExternalLink className='w-4 h-4' />
        </Button>
       </a>
      )}
     </div>
    </div>
   </div>

   <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
    {/* Left Column: Stats & Skills */}
    <div className='space-y-8'>
     {/* Status Card */}
     <div className='border border-primary/30 bg-primary/5 p-6'>
      <div className='font-mono text-xs text-muted-foreground uppercase mb-4'>
       System Status
      </div>
      <div className='space-y-4'>
       <div className='flex justify-between items-center border-b border-border/50 pb-2'>
        <span className='font-mono text-sm'>EXPERIENCE_LEVEL</span>
        <span className='font-bold'>{profile.experienceLevel}</span>
       </div>
       <div className='flex justify-between items-center border-b border-border/50 pb-2'>
        <span className='font-mono text-sm'>YRS_EXPERIENCE</span>
        <span className='font-bold'>{profile.yearsOfExperience}</span>
       </div>
       <div className='flex justify-between items-center border-b border-border/50 pb-2'>
        <span className='font-mono text-sm'>PROFILE_COMPLETENESS</span>
        <span className='font-bold text-primary'>
         {profile.profileCompleteness}%
        </span>
       </div>
      </div>
     </div>

     {/* Skills */}
     <div className='border border-border bg-background p-6'>
      <div className='flex items-center gap-2 mb-6'>
       <Code className='w-4 h-4 text-primary' />
       <h3 className='font-mono text-sm font-bold uppercase'>
        Technical_matrix
       </h3>
      </div>
      <div className='flex flex-wrap gap-2'>
       {profile.technicalSkills.map((skill, i) => (
        <Badge
         key={i}
         variant='secondary'
         className='rounded-none font-mono text-[10px] bg-secondary/10 hover:bg-secondary/20 border-secondary/50'
        >
         {skill.name}
        </Badge>
       ))}
      </div>
     </div>
    </div>

    {/* Center/Right: Details */}
    <div className='md:col-span-2 space-y-8'>
     {/* Directives & Configuration */}
     <div className='border border-primary/20 bg-background p-8 relative overflow-hidden'>
      <div className='absolute top-0 right-0 p-2 opacity-10'>
       <Settings className='w-24 h-24' />
      </div>
      <div className='flex items-center gap-2 mb-8 relative z-10'>
       <Target className='w-4 h-4 text-primary' />
       <h3 className='font-mono text-sm font-bold uppercase'>
        System_Directives
       </h3>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10'>
       <div>
        <h4 className='font-mono text-xs text-muted-foreground uppercase mb-2'>
         &gt; PARAMETERS
        </h4>
        <div className='space-y-2 font-mono text-sm'>
         <div className='flex justify-between border-b border-border/50 pb-1'>
          <span>COMM_STYLE:</span>
          <span className='text-primary uppercase'>
           {profile.communicationStyle || 'DEFAULT'}
          </span>
         </div>
         <div className='flex justify-between border-b border-border/50 pb-1'>
          <span>DIFFICULTY:</span>
          <span className='text-primary uppercase'>
           {profile.difficultyPreference || 'ADAPTIVE'}
          </span>
         </div>
         <div className='flex justify-between border-b border-border/50 pb-1'>
          <span>INTERVIEW_TYPE:</span>
          <span className='text-primary uppercase'>
           {profile.preferredInterviewTypes?.[0] || 'GENERAL'}
          </span>
         </div>
        </div>
       </div>

       <div>
        <h4 className='font-mono text-xs text-muted-foreground uppercase mb-2'>
         &gt; OBJECTIVES
        </h4>
        <div className='space-y-2'>
         {profile.interviewObjectives?.length > 0 ? (
          profile.interviewObjectives.map((obj, i) => (
           <div key={i} className='flex gap-2 text-sm font-mono'>
            <span className='text-primary/50'>[{i + 1}]</span>
            <span>{obj}</span>
           </div>
          ))
         ) : (
          <div className='text-xs text-muted-foreground italic font-mono'>
           NO OBJS CONFIGURED
          </div>
         )}
        </div>
       </div>

       {profile.focusAreas?.length > 0 && (
        <div className='col-span-1 md:col-span-2'>
         <h4 className='font-mono text-xs text-muted-foreground uppercase mb-2'>
          &gt; FOCUS_PROTOCOLS
         </h4>
         <div className='flex flex-wrap gap-2'>
          {profile.focusAreas.map((area, i) => (
           <Badge
            key={i}
            variant='outline'
            className='rounded-none font-mono text-[10px] border-primary/30 text-primary'
           >
            {area}
           </Badge>
          ))}
         </div>
        </div>
       )}
      </div>
     </div>

     {/* Experience - Collapsible */}
     <div className='border border-border bg-background'>
      <button
       onClick={() => setIsExperienceExpanded(!isExperienceExpanded)}
       className='w-full flex items-center justify-between p-8 hover:bg-muted/5 transition-colors text-left group'
      >
       <div className='flex items-center gap-2'>
        <Briefcase className='w-4 h-4 text-primary' />
        <h3 className='font-mono text-sm font-bold uppercase'>
         Career_History
        </h3>
       </div>
       <div className='flex items-center gap-4'>
        {!isExperienceExpanded && (
         <span className='font-mono text-xs text-muted-foreground uppercase tracking-widest'>
          [{profile.experience.length} RECORDS ARCHIVED]
         </span>
        )}
        {isExperienceExpanded ? (
         <ChevronUp className='w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors' />
        ) : (
         <ChevronDown className='w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors' />
        )}
       </div>
      </button>

      {isExperienceExpanded && (
       <div className='px-8 pb-8 space-y-8 relative border-l border-border/50 ml-10 pl-8 animate-in slide-in-from-top-2 duration-300'>
        {profile.experience.map((role, i) => (
         <div key={i} className='relative'>
          <div className='absolute left-[-37px] top-1 w-3 h-3 bg-background border border-primary rounded-full' />
          <div className='flex justify-between items-start mb-2'>
           <div>
            <h4 className='font-bold text-lg'>{role.title}</h4>
            <div className='text-sm text-primary font-mono'>{role.company}</div>
           </div>
           <div className='font-mono text-xs text-muted-foreground whitespace-nowrap'>
            {role.startDate?.toString()} -{' '}
            {role.endDate?.toString() || 'PRESENT'}
           </div>
          </div>
          <p className='text-sm text-muted-foreground leading-relaxed'>
           {role.description}
          </p>
          {role.achievements && role.achievements.length > 0 && (
           <ul className='mt-3 space-y-1'>
            {role.achievements.slice(0, 3).map((achievement, j) => (
             <li
              key={j}
              className='text-xs font-mono text-muted-foreground/80 flex items-start gap-2'
             >
              <span className='text-primary'>&gt;</span> {achievement}
             </li>
            ))}
           </ul>
          )}
         </div>
        ))}
       </div>
      )}
     </div>

     {/* Education */}
     <div className='border border-border bg-background p-8'>
      <div className='flex items-center gap-2 mb-8'>
       <GraduationCap className='w-4 h-4 text-primary' />
       <h3 className='font-mono text-sm font-bold uppercase'>Education_Log</h3>
      </div>

      <div className='space-y-6'>
       {profile.education.map((edu, i) => (
        <div
         key={i}
         className='flex justify-between items-start border-b border-border/50 pb-6 last:border-0 last:pb-0'
        >
         <div>
          {/* Use institution or fallback to school due to possible type alias */}
          <h4 className='font-bold'>{edu.institution || edu.school}</h4>
          <div className='text-sm text-muted-foreground'>
           {edu.degree} in {edu.fieldOfStudy || edu.field}
          </div>
         </div>
         <div className='font-mono text-xs text-muted-foreground'>
          {/* Safely handle Date conversion */}
          {edu.graduationDate?.toString() || edu.endDate?.toString()}
         </div>
        </div>
       ))}
      </div>
     </div>
    </div>
   </div>
  </AppLayout>
 );
}
